/**
 * useSessionMessages hook
 * React Query hook for fetching session messages with adaptive polling
 *
 * English tutor profile (default):
 *   - Starts at 2s, increases to 10s when idle, pauses when tab hidden
 *
 * Math Coach profile (sessionType === 'math_coach'):
 *   - 30s delay after image upload, 10s delay after text submission
 *   - Fixed 5s polling interval (no adaptive backoff)
 *   - Never pauses when last message is from tutor
 *   - Pauses only on tab hidden
 *   - 10s retry on error with isReconnecting flag
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getSessionMessages } from '@/services/sessions';
import type { SessionStudent,
  SessionMessagesResponse,
  Message,
  SessionSummary,
  QuestionProgress,
} from '@/types/session';

/**
 * Polling phase for Math Coach typing indicator (US-005).
 * Exported so MathCoachPage and MathCoachTypingIndicator can share the type.
 */
export type MathCoachPollingPhase =
  | 'image-delay'
  | 'text-delay'
  | 'polling'
  | 'reconnecting'
  | 'idle';

/**
 * Polling interval configuration — English tutor profile
 */
const POLLING_INTERVALS = {
  MIN: 2000, // 2 seconds - active polling when messages are coming
  MAX: 10000, // 10 seconds - maximum polling interval when idle
  INCREMENT: 2000, // 2 seconds - increase interval by this amount when no new messages
};

/**
 * Math Coach polling constants
 */
const MATH_COACH_POLLING = {
  INTERVAL: 5000, // Fixed 5s polling interval
  DELAY_IMAGE: 30_000, // 30s initial delay after image upload
  DELAY_TEXT: 10_000, // 10s initial delay after text submission
  RETRY_INTERVAL: 10_000, // 10s retry on network error
};

/**
 * Options for useSessionMessages hook
 */
export interface UseSessionMessagesOptions {
  /**
   * Session type — 'math_coach' activates the Math Coach polling profile.
   * Any other value (including undefined / null / unknown strings) uses the
   * English tutor profile as a safe default.
   */
  sessionType?: string;
}

/**
 * Return type for useSessionMessages hook
 */
export interface UseSessionMessagesReturn {
  /**
   * first-run-guide (T9): who this session BELONGS TO — name, picture, pebble total, and
   * whether THIS viewer may answer in it.
   *
   * Exists because a parent can now drive her child's session while signed in as herself, so
   * the viewer and the student can differ. undefined on payloads that predate the field, in
   * which case consumers keep their previous viewer-identity behaviour.
   */
  student?: SessionStudent;
  /** Full session data with messages */
  session: SessionMessagesResponse | undefined;

  /** Array of messages only */
  messages: Message[] | undefined;

  /** Reading passage text */
  passage: string | undefined;

  /** Reading passage title (from contentSnapshot); undefined/empty for legacy/MCP sessions */
  passageTitle?: string;

  /** Session status */
  status: string | undefined;

  /** Loading state - true during initial fetch */
  isLoading: boolean;

  /** Error object if fetch failed */
  error: Error | null;

  /** Whether polling is currently active */
  isPolling: boolean;

  /** Current polling interval in milliseconds */
  currentInterval: number;

  /** Whether waiting for tutor response */
  isWaitingForTutor: boolean;

  /** Total planned questions for the session (from backend metadata) */
  totalQuestions: number | undefined;

  /**
   * Session mode (mcp | serverless | undefined)
   * Undefined for legacy sessions or when session not yet loaded
   * Frontend treats undefined as MCP for backward compatibility
   */
  mode?: 'mcp' | 'serverless';

  /**
   * Call after a message is successfully submitted (Math Coach only).
   * 'image' starts a 30-second delay; 'text' starts a 10-second delay.
   * No-op when sessionType is not 'math_coach'.
   */
  notifyPollDelay: (type: 'image' | 'text') => void;

  /**
   * True when a Math Coach poll has failed and the hook is in error-retry mode.
   * Used by MathCoachPage to render the "Reconnecting…" indicator.
   */
  isReconnecting: boolean;

  /**
   * Current polling phase for Math Coach sessions (US-005).
   * Drives the MathCoachTypingIndicator component.
   * Always 'idle' for non-Math Coach sessions.
   */
  pollingPhase: MathCoachPollingPhase;

  /**
   * Star rating for this session (3 | 4 | 5 | null).
   * Passed through from the GET /sessions/{sessionId}/messages response.
   * Server-derived by derive_star_rating() (TS-014). The frontend must NEVER
   * recompute this value (DD-1, TO-LL-024). null when session is loading,
   * not completed, cancelled, or when the field is absent (legacy response).
   *
   * Implements US-019 T2.
   */
  starRating?: number | null;

  /**
   * Persisted, server-authoritative session summary. Passed through from the
   * GET /sessions/{sessionId}/messages response (BUG-20260710-162439 RC-002),
   * mirroring the starRating passthrough above. Pure passthrough — never
   * recomputed by the frontend (DD-1). null when session is loading/undefined
   * or no summary was persisted (active/cancelled/MCP/legacy sessions).
   */
  sessionSummary?: SessionSummary | null;

  /**
   * GAP-C (C-9): persisted question progress from the messages response, mirroring
   * the sessionSummary passthrough above. Pure passthrough — never recomputed here.
   * null when the session is loading/undefined, or for MCP and legacy sessions
   * which never carry it.
   */
  persistedQuestionProgress?: QuestionProgress | null;

  /**
   * BUG-20260731-163953 (RC-004/RC-005, T-12, 2026-07-31): passed through
   * from the GET /sessions/{sessionId}/messages response, mirroring the
   * starRating/sessionSummary/persistedQuestionProgress passthroughs
   * directly above. true when the backend detected the session's
   * conversation[] and questionResults[] are self-contradictory; the
   * consuming component (ConversationPanel, Phase 3) must then force every
   * structured input to free text. Defaults to false so callers never have
   * to null-check this specifically.
   */
  segmentationDegraded: boolean;
}

/**
 * Hook to fetch session messages with adaptive polling.
 *
 * English profile (default — any sessionType other than 'math_coach'):
 *   - Starts at 2s, backs off to 10s on idle polls
 *   - Pauses when tab is hidden
 *
 * Math Coach profile (sessionType === 'math_coach'):
 *   - Delays first poll by 30s (image) or 10s (text) after submission
 *   - Fixed 5s interval once polling begins — no backoff
 *   - Does NOT pause when last message is from tutor
 *   - Pauses only on tab hidden
 *   - On error: retries every 10s, exposes isReconnecting flag
 *
 * @param sessionId - The session ID to fetch messages for
 * @param options   - Optional config; pass { sessionType: 'math_coach' } for Math Coach
 * @returns Object containing session data, polling state, loading state, error, and helpers
 */
export function useSessionMessages(
  sessionId: string,
  options?: UseSessionMessagesOptions
): UseSessionMessagesReturn {
  const sessionType = options?.sessionType;
  const isMathCoach = sessionType === 'math_coach';

  // ---- Shared: tab visibility ----
  const [isTabVisible, setIsTabVisible] = useState(true);
  const isTabVisibleRef = useRef(isTabVisible);

  useEffect(() => {
    isTabVisibleRef.current = isTabVisible;
  }, [isTabVisible]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const visible = document.visibilityState === 'visible';
      setIsTabVisible(visible);

      // Reset English interval to MIN when tab becomes visible
      if (visible && !isMathCoach) {
        setPollingInterval(POLLING_INTERVALS.MIN);
      }
    };

    setIsTabVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isMathCoach]);

  // ---- English profile: adaptive interval state ----
  const [pollingInterval, setPollingInterval] = useState(POLLING_INTERVALS.MIN);
  const pollingIntervalRef = useRef(pollingInterval);

  useEffect(() => {
    pollingIntervalRef.current = pollingInterval;
  }, [pollingInterval]);

  // ---- Math Coach profile: delay + error refs ----
  const mathCoachDelayEndRef = useRef<number | null>(null); // epoch ms when delay expires
  const mathCoachRetryCountRef = useRef<number>(0); // tracks consecutive errors
  // mathCoachIsErrorRef drives refetchInterval (needs closure access without stale state)
  const mathCoachIsErrorRef = useRef<boolean>(false); // currently in error-retry mode
  // mathCoachIsErrorState drives isReconnecting UI (needs state to trigger re-render)
  const [mathCoachIsErrorState, setMathCoachIsErrorState] = useState<boolean>(false);
  const delayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // delayType tracks which delay is active so MathCoachTypingIndicator can show
  // the correct static text. useState (not ref) so it survives tab switches (AC-005.6).
  const [delayType, setDelayType] = useState<'image' | 'text' | null>(null);

  const queryClient = useQueryClient();

  // ---- Math Coach: notifyPollDelay ----
  const notifyPollDelay = useCallback(
    (type: 'image' | 'text') => {
      if (!isMathCoach) return;
      const delayMs =
        type === 'image' ? MATH_COACH_POLLING.DELAY_IMAGE : MATH_COACH_POLLING.DELAY_TEXT;
      const endTime = Date.now() + delayMs;
      mathCoachDelayEndRef.current = endTime;
      setDelayType(type); // US-005: track which delay is active for typing indicator
      mathCoachIsErrorRef.current = false;
      mathCoachRetryCountRef.current = 0;
      setMathCoachIsErrorState(false);

      // Cancel any existing delay timer before setting a new one
      if (delayTimerRef.current !== null) {
        clearTimeout(delayTimerRef.current);
      }

      // Schedule the first poll at the exact delay boundary.
      // queryClient.invalidateQueries triggers a re-fetch after the delay.
      delayTimerRef.current = setTimeout(() => {
        mathCoachDelayEndRef.current = null; // delay over — allow polling
        setDelayType(null); // US-005: delay expired — transition to polling phase
        queryClient.invalidateQueries({ queryKey: ['session', sessionId, 'messages'] });
      }, delayMs);
    },
    [isMathCoach, sessionId, queryClient]
  );

  // ---- Cleanup delay timer on unmount ----
  useEffect(() => {
    return () => {
      if (delayTimerRef.current !== null) {
        clearTimeout(delayTimerRef.current);
      }
    };
  }, []);

  // ---- Fetch session messages with React Query ----
  const {
    data: session,
    isLoading,
    error,
    dataUpdatedAt,
  } = useQuery<SessionMessagesResponse, Error>({
    queryKey: ['session', sessionId, 'messages'],
    queryFn: () => getSessionMessages(sessionId),
    staleTime: 0,
    refetchInterval: (query) => {
      // 1. Never poll if tab is hidden (shared for both profiles)
      if (!isTabVisibleRef.current) {
        return false;
      }

      const data = query.state.data;

      // 2. Never poll completed, cancelled, or ended_by_parent sessions (shared)
      // CCM-01 reader (US-016): 'cancelled' is terminal — polling must stop, matching 'completed'.
      // TS-007 (T5, AC-TS007.1): 'ended_by_parent' (parent force-ended) is ALSO terminal —
      // polling must stop the moment a next poll observes it.
      if (
        data?.status === 'completed' ||
        data?.status === 'cancelled' ||
        data?.status === 'ended_by_parent'
      ) {
        return false;
      }

      // 3. Never poll serverless sessions (shared)
      if (data?.mode === 'serverless') {
        return false;
      }

      // 4. Math Coach profile
      if (isMathCoach) {
        // 4a. Inside the initial delay window — do not poll yet
        if (mathCoachDelayEndRef.current !== null && Date.now() < mathCoachDelayEndRef.current) {
          return false;
        }
        // 4b. Error-retry mode — poll every 10 seconds
        if (mathCoachIsErrorRef.current) {
          return MATH_COACH_POLLING.RETRY_INTERVAL;
        }
        // 4c. Normal polling: fixed 5-second interval
        return MATH_COACH_POLLING.INTERVAL;
      }

      // 5. English profile — existing adaptive logic (unchanged)
      return pollingIntervalRef.current;
    },
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    retry: 0,
    // Fix: 2026-01-23 - Changed from retry: 3 to retry: 0 to prevent test timeouts
    // in error handling tests (TC-WIDGET-US006-012, TC-WIDGET-US006-013)
  });

  // ---- Math Coach: track error state via useEffect (RQ v5 — no onError) ----
  // Both ref (for refetchInterval closure) and state (for reactive isReconnecting) are updated.
  useEffect(() => {
    if (!isMathCoach) return;
    const isError = !!error;
    mathCoachIsErrorRef.current = isError;
    setMathCoachIsErrorState(isError);
  }, [error, isMathCoach]);

  // ---- Derived state ----
  const lastMessage = session?.messages?.[session.messages.length - 1];
  const isWaitingForTutor = !lastMessage || lastMessage.role === 'student';
  // CCM-01 reader (US-016): 'cancelled' is terminal alongside 'completed' — isSessionActive
  // must return false for both so isPolling stops and dependent UI reflects terminal state.
  // TS-007 (T5, AC-TS007.1): 'ended_by_parent' is ALSO terminal — isSessionActive must be
  // false so isPolling stops and SessionChatPage renders the distinct terminal message (T6).
  const isSessionActive =
    session?.status !== 'completed' &&
    session?.status !== 'cancelled' &&
    session?.status !== 'ended_by_parent';
  const isPolling = isTabVisible && isSessionActive && session?.mode !== 'serverless';

  // Math Coach reconnecting indicator — state-driven so it triggers a re-render
  const isReconnecting = isMathCoach && mathCoachIsErrorState;

  // ---- US-005: Derive pollingPhase for MathCoachTypingIndicator ----
  // useMemo ensures re-computation only when reactive inputs change.
  // mathCoachDelayEndRef is a ref (not state), but delayType state changes at the
  // same moment the ref changes, so the condition is evaluated correctly.
  const pollingPhase = useMemo((): MathCoachPollingPhase => {
    if (!isMathCoach) return 'idle';
    if (isReconnecting) return 'reconnecting';
    if (
      delayType !== null &&
      mathCoachDelayEndRef.current !== null &&
      Date.now() < mathCoachDelayEndRef.current
    ) {
      return delayType === 'image' ? 'image-delay' : 'text-delay';
    }
    const lastMsg = session?.messages?.[session.messages.length - 1];
    if (!lastMsg || lastMsg.role === 'parent') return 'polling';
    return 'idle';
  }, [isMathCoach, isReconnecting, delayType, session?.messages]);

  // ---- English profile: adaptive interval backoff (Math Coach bypasses this) ----
  const previousDataUpdatedAtRef = useRef<number>(0);
  const previousMessageCountRef = useRef<number>(0);
  const messageCount = session?.messages?.length ?? 0;

  useEffect(() => {
    // Math Coach uses fixed interval — skip adaptive backoff entirely
    if (isMathCoach) return;

    // Skip if no data yet (initial loading)
    if (!dataUpdatedAt || !session?.messages) {
      return;
    }

    // Check if this is an actual data fetch (not just a re-render)
    const isNewFetch = dataUpdatedAt !== previousDataUpdatedAtRef.current;
    if (!isNewFetch) {
      return;
    }

    // Mark this fetch as processed
    previousDataUpdatedAtRef.current = dataUpdatedAt;

    // Check if new messages arrived compared to previous fetch
    const hasNewMessages = messageCount > previousMessageCountRef.current;

    if (hasNewMessages) {
      // New messages detected - reset to minimum interval for active polling
      setPollingInterval(POLLING_INTERVALS.MIN);
    } else if (previousMessageCountRef.current > 0) {
      // No new messages on this poll (not initial load) - backoff
      setPollingInterval((prevInterval) => {
        const newInterval = prevInterval + POLLING_INTERVALS.INCREMENT;
        return Math.min(newInterval, POLLING_INTERVALS.MAX);
      });
    }

    // Update previous count for next comparison
    previousMessageCountRef.current = messageCount;
  }, [dataUpdatedAt, messageCount, session?.messages, isMathCoach]);

  return {
    session,
    /**
     * first-run-guide (T9): who this session BELONGS TO. Passthrough only.
     *
     * The tutoring screen must render THIS person, not whoever is viewing, because a parent
     * can now drive her child's session while signed in as herself. undefined on older
     * payloads, in which case the screen falls back to its previous viewer-identity behaviour.
     */
    student: session?.student,
    messages: session?.messages,
    passage: session?.passage,
    passageTitle: session?.passageTitle,
    status: session?.status,
    // US-019 T2: surface server-derived star rating from the messages response.
    // Passthrough only — frontend must never recompute (DD-1, TO-LL-024, TO-LL-044).
    // null when session is undefined (loading/error) or rating is absent/null.
    starRating: session?.starRating ?? null,
    // BUG-20260710-162439 (RC-002): passthrough of the persisted, server-authoritative
    // session summary, mirroring the starRating passthrough directly above.
    sessionSummary: session?.sessionSummary ?? null,
    // GAP-C (C-9): passthrough of the persisted question progress, mirroring the
    // sessionSummary passthrough directly above. This is the ONLY path by which
    // `phase` survives a page load — the tutor-turn response is in-memory and is
    // never replayed.
    persistedQuestionProgress: session?.questionProgress ?? null,
    // BUG-20260731-163953 (RC-004/RC-005, T-12): passthrough, mirroring the
    // three passthroughs directly above. Defaults to false (undefined ==
    // healthy) rather than null, since it drives a boolean render decision.
    segmentationDegraded: session?.segmentationDegraded ?? false,
    isLoading,
    error,
    isPolling,
    currentInterval: pollingInterval,
    isWaitingForTutor,
    totalQuestions: session?.totalQuestions,
    mode: session?.mode,
    notifyPollDelay,
    isReconnecting,
    pollingPhase,
  };
}
